// Due Date Control — DASHBOARD mode (`<base>/dashboard`, under DueDateLayout).
//
// The pattern is PATTERNS.md BP-15 (View 1100) inside <DashboardPage>, the
// `.an-home` vocabulary of src/design/aeronet-home.css, top to bottom:
//
//   1. hero     — open due dates (a server COUNT), Live snapshot pill, refresh, View due dates
//   2. KPI row  — Overdue · Due in 7 days · Due in 30 days · No due date · Templates; each
//                 tile OPENS THE LIST FILTERED to what it counts (BP-93, dueWindows.ts)
//   3. two-col  — Overdue beside Next due, each row opening its record
//   4. two-col  — By section beside By assignee (open / overdue / due ≤30d)
//
// No tab row: there is no client-side slice of this payload worth a tab, and a
// tab added for the shape's sake is decoration (BP-14).
//
// ── WHERE EVERY FIGURE COMES FROM ────────────────────────────────────────────
// ONE read, GET /v6/duedate/summary: every count is computed by the server over
// OPEN (status=1) active ASSIGNED rows, permission-scoped like the list. The
// Overdue / Next due / By section / By assignee lists are the server's (capped
// at 10). Nothing here is a reduce over a page. A failed read is a failed page
// — never zeros. Mirrors src/subscriptions/SubscriptionsDashboard.tsx (tiles,
// rows) and src/ata/ATADashboard.tsx (loading / error inside the shell).
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// The page shell: grey canvas + `.an-home` root (BP-15 step 0, H277).
import DashboardPage from "../design/DashboardPage";
// The blocks (hero, BP-93 tiles, record rows, breakdown), shared with the Action Register.
import {
  BreakdownTable,
  DashboardHero,
  KpiTile as Kpi,
  RecordRowList,
  type RecordRow,
} from "../design/DashboardBlocks";
import { filteredListToAll } from "../components/datagrid/urlFilter";
import { useListingRecordTarget } from "../components/datagrid/ListingRouteBase";
import { formatTenantDate, useTenantDateFormat } from "../utils/tenantDate";

import { DUE_WINDOW_FIELD, OPEN_STATUS, type DueWindow } from "./dueWindows";
import { getDueDateSummary } from "./dueDateSummary";
import type { DueDateSummary, DueDateSummaryItem } from "./type";

/** A window tile's target: the Due Dates list, open rows in that window. */
const windowTo = (window: DueWindow) =>
  filteredListToAll({ status: OPEN_STATUS, [DUE_WINDOW_FIELD]: window });

/** The Overdue / Next due rows: title, assignee · section, due date, days. */
const toRows = (items: DueDateSummaryItem[], datePattern: string | undefined): RecordRow[] =>
  items.map((r) => ({
    id: r.id,
    title: r.title || "Untitled",
    sub: `${r.assigned_name || "Unassigned"} · ${r.section_name || "No section"}`,
    date: formatTenantDate(r.required, datePattern),
    days: r.days,
  }));

interface State {
  loaded: boolean;
  data: DueDateSummary | null;
}

const DueDateDashboard: React.FC = () => {
  const navigate = useNavigate();
  // BP-41: the record page is a sibling of the modes; the layout declares the base.
  const recordTarget = useListingRecordTarget();
  // BP-83: a displayed date wears the TENANT's format; `undefined` until config loads.
  const datePattern = useTenantDateFormat();
  const [state, setState] = useState<State>({ loaded: false, data: null });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loaded: false }));
    try {
      setState({ loaded: true, data: await getDueDateSummary() });
    } catch {
      // Honest failure — never fall back to zeros or seeded rows.
      setState({ loaded: true, data: null });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openRecord = useCallback((id: number) => navigate(recordTarget(id)), [navigate, recordTarget]);

  if (!state.loaded && !state.data) {
    return (
      <DashboardPage>
        <div className="empty">Loading…</div>
      </DashboardPage>
    );
  }

  const d = state.data;
  if (!d) {
    return (
      <DashboardPage>
        <div className="warnstrip warnstrip--error">Failed to load dashboard.</div>
      </DashboardPage>
    );
  }

  return (
    <DashboardPage data-testid="duedate-dashboard">
      {/* 1. HERO — a server COUNT. */}
      <DashboardHero
        value={d.open_total}
        caption={<>open due dates, of {d.assigned_total.toLocaleString()} assigned</>}
        onRefresh={load}
        loaded={state.loaded}
        actionLabel="View due dates"
        onAction={() => navigate("../list", { relative: "path" })}
      />

      {/* 2. KPI ROW — each tile opens the list filtered to what it counts (BP-93). */}
      <div className="kpis">
        <Kpi
          id="overdue"
          label="Overdue"
          value={d.overdue}
          sub="open, past the due date"
          to={windowTo("overdue")}
          tone={d.overdue > 0 ? "neg" : undefined}
        />
        <Kpi
          id="due-7"
          label="Due in 7 days"
          value={d.due_7}
          sub="open, due this week"
          to={windowTo("due_7")}
          tone={d.due_7 > 0 ? "warn" : undefined}
        />
        <Kpi id="due-30" label="Due in 30 days" value={d.due_30} sub="open, due within 30 days" to={windowTo("due_30")} />
        <Kpi id="no-due-date" label="No due date" value={d.no_due_date} sub="open, no date set" to={windowTo("none")} />
        <Kpi id="templates" label="Templates" value={d.templates_total} sub="requirements with no assignee" to="../templates" />
      </div>

      {/* 3. TWO-COL — what is late beside what is next. */}
      <div className="two-col">
        <div className="card">
          <div className="eyebrow">Overdue</div>
          <div className="card__title">Open past their due date</div>
          <RecordRowList
            rows={toRows(d.overdue_items, datePattern)}
            empty="Nothing overdue."
            open={openRecord}
            testId="overdue-items"
          />
        </div>
        <div className="card">
          <div className="eyebrow">Next due</div>
          <div className="card__title">Coming up</div>
          <RecordRowList
            rows={toRows(d.upcoming, datePattern)}
            empty="Nothing coming up."
            open={openRecord}
            testId="upcoming-items"
          />
        </div>
      </div>

      {/* 4. TWO-COL — where the open work sits. */}
      <div className="two-col">
        <div className="card">
          <div className="eyebrow">By section</div>
          <div className="card__title">Open due dates by section</div>
          <BreakdownTable
            nameHeader="Section"
            empty="No open due dates."
            testId="by-section"
            rows={d.by_section.map((r) => ({
              key: r.section_id,
              name: r.section_name || "No section",
              open: r.open,
              overdue: r.overdue,
              due_30: r.due_30,
            }))}
          />
        </div>
        <div className="card">
          <div className="eyebrow">By assignee</div>
          <div className="card__title">Open due dates by person</div>
          <BreakdownTable
            nameHeader="Assignee"
            empty="No open due dates."
            testId="by-assignee"
            rows={d.by_assignee.map((r) => ({
              key: r.uid,
              name: r.name || "Unknown user",
              open: r.open,
              overdue: r.overdue,
              due_30: r.due_30,
            }))}
          />
        </div>
      </div>

      <span className="footnote" data-testid="dashboard-footnote">
        Every figure but Templates counts open due dates assigned to people, as the server read them on{" "}
        {formatTenantDate(d.today, datePattern) || d.today}. Each card lists up to 10 rows; a tile opens the full list.
      </span>
    </DashboardPage>
  );
};

export default DueDateDashboard;
